import { isDecimal } from "class-validator";
import Mexp from "math-expression-evaluator";

export interface Environment {
	lang: string;
	// in general, apiUrl's value is empty string because we return index.html from RController.cs
	apiUrl: string;
}

const mexp = new Mexp();

export let LOCALE_DECIMAL_SEPARATOR: string | undefined;
export let LOCALE_GROUP_SEPARATOR: string | undefined;

const unifyCode = (value: string) => {
	return (
		value
			// replacing different kind of dashes into minus
			.replace(/\u2010|\u2011|\u2012|\u2013|\u2014|\u2015|\u2212|\u002D|\uFE58|\uFE63|\uFF0D/g, "-")
			// replacing non-breaking space into space, it is the default seprator in Intl
			.replace(/\u00A0/g, " ")
	);
};

export function setup({ lang }: Partial<Environment>) {
	if (!lang) {
		return;
	}
	LOCALE_DECIMAL_SEPARATOR = undefined;
	LOCALE_GROUP_SEPARATOR = undefined;
	new Intl.NumberFormat(lang).formatToParts(1000000.25).forEach(part => {
		if (part.type === "group") {
			LOCALE_GROUP_SEPARATOR = unifyCode(part.value);
		} else if (part.type === "decimal") {
			LOCALE_DECIMAL_SEPARATOR = unifyCode(part.value);
		}
	});
}

const getLastSeparator = (value: string) => {
	const parts = value.split(/[^\d]/g);
	return value[value.length - parts[parts.length - 1].length - 1];
};

const parseSeparatorsInNumber = (value: string, lastSeparator: string) => {
	if (value.split("").filter(c => c === lastSeparator).length > 1) {
		if (lastSeparator === LOCALE_DECIMAL_SEPARATOR) {
			return NaN;
		} else {
			return parseFloat(value.replace(/[^\d]/g, ""));
		}
	} else {
		const normalizedValue = value.replace(new RegExp(`[^\\d${lastSeparator}]`, "g"), "");
		return parseFloat(normalizedValue.replace(lastSeparator, "."));
	}
};

const parseSeparatorInNumber = (value: string, lastSeparator: string) => {
	// NOTE: We allow flexible decimal separator, because people usually input less than thousand
	if (LOCALE_GROUP_SEPARATOR === lastSeparator) {
		return parseFloat(value.replace(/[^\d]/g, ""));
	} else {
		return parseFloat(value.replace(/[^\d]/g, "."));
	}
};

const parseValidFormatNumber = (value: string) => {
	const lastSeparator = getLastSeparator(value);
	const parts = value.split(/[^\d]/g);

	if (parts.length > 2) {
		return parseSeparatorsInNumber(value, lastSeparator);
	} else {
		return parseSeparatorInNumber(value, lastSeparator);
	}
};

export const parseNumber = (value: string | number) => {
	if (typeof value === "number") {
		return value;
	}
	const cleanedValue = unifyCode(value).trim();

	if (/^[-+]?\d+$/.test(cleanedValue)) {
		return parseFloat(cleanedValue);
		// https://en.wikipedia.org/wiki/Decimal_separator
	} else if (/^[-+]?[\d\s,.']+$/.test(cleanedValue)) {
		const firstChar = cleanedValue[0];
		if (firstChar === "-") {
			return -parseValidFormatNumber(cleanedValue.substr(1));
		} else if (firstChar === "+") {
			return parseValidFormatNumber(cleanedValue.substr(1));
		} else {
			return parseValidFormatNumber(cleanedValue);
		}
	} else {
		return NaN;
	}
};

export const parseString = (value: string | number) => {
	if (typeof value === "number") {
		return value;
	}
	const cleanedValue = unifyCode(value).trim();
	if (/^[-+]?\d+$/.test(cleanedValue)) {
		return parseFloat(cleanedValue);
		// https://en.wikipedia.org/wiki/Decimal_separator
	} else if (/^[-+]?[\d\s,.']+$/.test(cleanedValue)) {
		const firstChar = cleanedValue[0];
		if (firstChar === "-") {
			return -parseValidFormatNumber(cleanedValue.substr(1));
		} else if (firstChar === "+") {
			return parseValidFormatNumber(cleanedValue.substr(1));
		} else {
			return parseValidFormatNumber(cleanedValue);
		}
	} else if (/[+/*,.-]$/.test(cleanedValue)) {
		// return the same when the user is in middle of expression (string ending with a mathematical operator)
		return cleanedValue;
	} else {
		try {
			const cleandValueWithoutComma =
				LOCALE_DECIMAL_SEPARATOR === ","
					? cleanedValue.replace(/[,]/g, ".")
					: LOCALE_DECIMAL_SEPARATOR === "."
						? cleanedValue.replace(/[,]/g, "")
						: cleanedValue;
			const answer = mexp.eval(cleandValueWithoutComma, [], {});
			return Number(Number(answer).toFixed(2));
		} catch (error) {
			return NaN;
		}
	}
};

export function isDecimalString(value: string): boolean {
	if (!value || value.trim() === "") {
		return false;
	}
	const toUSLocale = value.replace(/[^0-9.,]/g, "").replace(/,/g, ".");
	return isDecimal(toUSLocale);
}
